// Every job in the process's company, for the Job Allocation picker, plus
// on-demand resolution of a job's pay details.
//
// The company list is enough to *offer* a job. It is not always enough to cost
// one — so when a job outside the eligible set is actually picked, GET /job/{id}
// is consulted for the authoritative `hourlyRate`, `currencyType` and
// `staff_count`. That request is made once per job and cached: several tasks can
// reassign to the same job, and none of them should re-fetch it.

import React from "react";
import { getJobById, getJobsByCompanyId } from "../services/jobService";
import {
  indexCompanyJobs,
  normalizeCompanyJob,
} from "../utils/whatif/whatIfJobOptions";

export default function useCompanyJobs(companyId) {
  const [jobsById, setJobsById] = React.useState(() => new Map());
  const [error, setError] = React.useState(null);

  // Resolved pay details, keyed by job id. A ref rather than state: it is a
  // cache, and filling it must not re-render every open picker.
  const detailsRef = React.useRef(new Map());

  React.useEffect(() => {
    let cancelled = false;
    detailsRef.current = new Map();

    if (!companyId) {
      setJobsById(new Map());
      return undefined;
    }

    (async () => {
      try {
        const jobs = await getJobsByCompanyId(companyId);
        if (!cancelled) {
          setJobsById(indexCompanyJobs(jobs));
          setError(null);
        }
      } catch (caught) {
        console.error("Failed to load company jobs for What-if:", caught);
        // Not fatal: the picker falls back to the eligible list alone, which is
        // exactly how it behaved before company jobs were offered.
        if (!cancelled) {
          setJobsById(new Map());
          setError("Could not load the company's other jobs.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const resolveJobDetails = React.useCallback(async (jobId) => {
    const key = String(jobId);
    if (detailsRef.current.has(key)) return detailsRef.current.get(key);

    try {
      const record = await getJobById(jobId);
      const details = normalizeCompanyJob(record);
      if (details) detailsRef.current.set(key, details);
      return details;
    } catch (caught) {
      console.error(`Failed to load job ${jobId} for What-if:`, caught);
      return null;
    }
  }, []);

  return { jobsById, error, resolveJobDetails };
}
